import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import { transferVehicle } from "@majourneys/booking";
import { useDisplayPrice } from "@majourneys/currency";
import { TRANSFER_OUTSIDE_FEE, type AiRideLayout, type AiRideRoute } from "@majourneys/shared";
import { panelStyle } from "../../../shared/ui/card-frame";
import type { ChatLang } from "../hooks/use-ai-chat";
import { LayoutButton, placeHref } from "./layout-parts";
import { CONTROL_STYLE, SOFT } from "./soft-tokens";

const TRIP_KEY: Record<AiRideRoute["trip"], string> = {
  one_way: "ai_concierge.ride_one_way",
  return: "ai_concierge.ride_return",
  hourly: "ai_concierge.ride_hourly",
};

/**
 * Chat kit C13, Ride: one framed card per matching transfer route (at most
 * two). A navy coin with the car, the route's own name and line, its trip
 * type, the three vehicles and their prices, the outside-city note, and
 * "Book this ride" to the transport hub with the route preselected (routes
 * have no pages of their own; `placeHref` knows that).
 *
 * The route carries a name and a line, not separate from and to fields, so
 * the card shows what the route says rather than splitting it into two ends
 * it never sent. Nothing here promises more than the hub does: no
 * meet-and-greet, no waiting time, no journey time. Prices are per vehicle,
 * in the visitor's currency like every other price in the chat, and paid to
 * the driver on arrival (transfers are pay on arrival).
 */
export function RideView({ ride, lang }: { ride: AiRideLayout; lang: ChatLang }) {
  return (
    <div className="flex flex-col gap-3">
      {ride.routes.map((route) => (
        <RouteCard key={route.slug} route={route} lang={lang} />
      ))}
    </div>
  );
}

function RouteCard({ route, lang }: { route: AiRideRoute; lang: ChatLang }) {
  const { t } = useTranslation();
  const displayPrice = useDisplayPrice();
  const href = placeHref(
    { slug: route.slug, citySlug: route.citySlug, categorySlug: route.categorySlug, segment: null },
    lang,
  );

  return (
    <section
      style={{ ...panelStyle(18, "#fff"), padding: 12 }}
      aria-label={route.title}
      data-testid={`ride-card-${route.slug}`}
    >
      <div className="flex items-center gap-2.5">
        <span
          aria-hidden="true"
          className="grid h-8 w-8 shrink-0 place-items-center rounded-full"
          style={{ background: SOFT.inkGradient, color: SOFT.gold }}
        >
          <Icon name="ri-car-line" size={15} />
        </span>
        <div className="min-w-0">
          <h3 className="text-start text-[14px] font-semibold leading-[19px]" style={{ color: SOFT.ink }}>
            {route.title}
          </h3>
          <p className="text-start text-[11.5px]" style={{ color: SOFT.body }}>
            {t(TRIP_KEY[route.trip])}
          </p>
        </div>
      </div>

      {route.subtitle ? (
        <p className="mt-2.5 text-start text-[12.5px] leading-[18px]" style={{ color: SOFT.ink }}>
          {route.subtitle}
        </p>
      ) : null}

      <ul className="mt-3 flex flex-col gap-1.5">
        {route.vehicles.map((v) => {
          const vehicle = transferVehicle(v.maxGuests);
          return (
            <li
              key={v.maxGuests}
              className="flex items-center gap-3 rounded-[12px] px-2.5 py-2"
              style={CONTROL_STYLE}
            >
              <span className="inline-flex h-8 w-12 shrink-0 items-center justify-center">
                <img
                  src={vehicle.icon}
                  alt=""
                  width={42}
                  height={28}
                  loading="lazy"
                  decoding="async"
                  className="h-7 w-[42px] object-contain"
                />
              </span>
              <span className="min-w-0 flex-1 text-start">
                <span className="block text-[13px] font-semibold" style={{ color: SOFT.ink }}>
                  {t(vehicle.nameKey)}
                </span>
                <span className="block text-[11.5px]" style={{ color: SOFT.body }}>
                  {t("ai_concierge.ride_up_to", { count: v.maxGuests })}
                </span>
              </span>
              <span className="shrink-0 text-end">
                <span
                  className="block whitespace-nowrap text-[14px] font-semibold tabular-nums"
                  style={{ color: SOFT.ink }}
                >
                  {displayPrice(v.priceMad)}
                </span>
                <span className="block text-[11px]" style={{ color: SOFT.body }}>
                  {t("ai_concierge.ride_per_vehicle")}
                </span>
              </span>
            </li>
          );
        })}
      </ul>

      <div className="mt-3 border-t pt-2.5" style={{ borderColor: SOFT.hairline }}>
        <p className="text-start text-[12px]" style={{ color: SOFT.body }}>
          {t("ai_concierge.ride_pay_driver")}
        </p>
        <p className="mt-1 text-start text-[12px] leading-[17px]" style={{ color: SOFT.goldInk }}>
          {t("transport.outside_note", { fee: displayPrice(TRANSFER_OUTSIDE_FEE) })}
        </p>
      </div>

      <div className="mt-3 flex">
        <LayoutButton href={href} primary>
          {t("ai_concierge.action_book_ride")}
        </LayoutButton>
      </div>
    </section>
  );
}
